/**
 * This config setup allows us to debug/QA the experience by adjusting
 * some settings via localStorage overrides.
 *
 * Otherwise, we'd have to wait for days to observe cache expiry/refresh behavior,
 * and in-source-product minimum activity times.
 */
export const getConfig = (): {
	readonly CACHE_TIME_UNTIL_EXPIRY: number;
	readonly CACHE_TIME_UNTIL_REFRESH: number;
	readonly TIME_IN_SOURCE_PRODUCT_BEFORE_ELIGIBLE: number;
} => CONSTANTS_AS_DEBUGGABLE_GETTERS;

const timespan = ({ days = 0, minutes = 0, seconds = 0 }) =>
	((days * 24 * 60 + minutes) * 60 + seconds) * 1000;

const CONSTANTS = {
	TIME_IN_SOURCE_PRODUCT_BEFORE_ELIGIBLE: timespan({ days: 3 }),

	/**
	 * When the customer visits after this time since we last wrote data to the cache,
	 * we'll refresh the cache: refetch the required data.
	 */
	CACHE_TIME_UNTIL_REFRESH: timespan({ days: 7 }),

	/**
	 * When the customer visits after this time since we last wrote data to the cache,
	 * we'll consider the cache expired and invalid, and not eligible to see the experiment
	 * (the user will not see the treatment on that visit).
	 * We will refresh the cache at that time, but even if that comes back as eligible,
	 * it will only affect future visits.
	 */
	CACHE_TIME_UNTIL_EXPIRY: timespan({ days: 10 }),
};

const CONSTANTS_AS_DEBUGGABLE_GETTERS = {
	get TIME_IN_SOURCE_PRODUCT_BEFORE_ELIGIBLE() {
		return getDebuggableTiming('TIME_IN_SOURCE_PRODUCT_BEFORE_ELIGIBLE');
	},
	get CACHE_TIME_UNTIL_REFRESH() {
		return getDebuggableTiming('CACHE_TIME_UNTIL_REFRESH');
	},
	get CACHE_TIME_UNTIL_EXPIRY() {
		return getDebuggableTiming('CACHE_TIME_UNTIL_EXPIRY');
	},
};

function getDebuggableTiming(key: keyof typeof CONSTANTS): number {
	if (typeof localStorage !== 'undefined' && localStorage) {
		const debugLocalStorageKey = `invitedAppShortcutsDebug_${key}_seconds`;
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		const overrideValue = parseInt(localStorage[debugLocalStorageKey], 10);
		if (typeof overrideValue === 'number' && overrideValue >= 0) {
			return timespan({ seconds: overrideValue });
		}
	}
	return CONSTANTS[key];
}
